import { Navigate, Route, Routes, useLocation } from 'react-router';
import Loading from '../common/lib/Loading';
import PageNotFound from '../common/lib/PageNotFound';
import useInitialized from '../common/lib/useInitialized';
import type { MultiLang } from '../config';
import XwordsUtils from './lib/XwordsUtils';
import XwordsCategory from './XwordsCategory';
import XwordsEntry from './XwordsEntry';
import XwordsIndex from './XwordsIndex';
import XwordsLetter from './XwordsLetter';
import XwordsSearch from './XwordsSearch';

interface Props {
    lang: MultiLang;
}

const useParams = () => new URLSearchParams(useLocation().search);

const intParam = (params: URLSearchParams, key: string, defaultValue: string): number => {
    const value = params.get(key) || defaultValue;
    return /^[0-9]+$/.test(value) ? parseInt(value, 10) : 0;
};

const CategoryPage = (props: Props) => {
    const params = useParams();
    return (
        <XwordsCategory
            lang={props.lang}
            catId={intParam(params, 'categoryID', '0')}
            start={intParam(params, 'start', '0')}
        />
    );
};

const LetterPage = (props: Props) => {
    const params = useParams();
    return (
        <XwordsLetter
            lang={props.lang}
            catId={intParam(params, 'categoryID', '0')}
            initial={params.get('init') || ''}
            start={intParam(params, 'start', '0')}
        />
    );
};

const EntryPage = (props: Props) => {
    const params = useParams();
    return <XwordsEntry lang={props.lang} entryId={intParam(params, 'entryID', '0')} />;
};

const SearchPage = (props: Props) => {
    const params = useParams();
    return (
        <XwordsSearch
            lang={props.lang}
            type={intParam(params, 'type', '4')}
            catId={intParam(params, 'catID', '0')}
            andor={params.get('andor') || 'OR'}
            term={params.get('term') || ''}
            start={intParam(params, 'start', '0')}
        />
    );
};

const IndexRedirect = () => {
    const { search, hash } = useLocation();
    return <Navigate to={`${XwordsUtils.getIndexUrl()}${search}${hash}`} replace />;
};

const Xwords = (props: Props) => {
    const { lang } = props;
    const status = useInitialized(XwordsUtils.initialize);
    if (status === null) {
        return <Loading />;
    }
    if (!status) {
        return <PageNotFound lang={lang} />;
    }
    return (
        <Routes>
            <Route index element={<IndexRedirect />} />
            <Route path="index.php" element={<XwordsIndex lang={lang} />} />
            <Route path="category.php" element={<CategoryPage lang={lang} />} />
            <Route path="letter.php" element={<LetterPage lang={lang} />} />
            <Route path="entry.php" element={<EntryPage lang={lang} />} />
            <Route path="search.php" element={<SearchPage lang={lang} />} />
            <Route path="*" element={<PageNotFound lang={lang} />} />
        </Routes>
    );
};

export default Xwords;
